import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import InnovationBulb from "./assets/innovation-bulb.svg";
import "./styles.scss";

export interface MyTripsCsatSurveyModalProps {
  isOpen: boolean;
  onComplete: () => void;
  onDismiss: (step: number) => void;
  onResponse: (response: {
    question: string;
    questionId: string;
    responseValue: number;
    step: number;
  }) => void;
}

const questions = [
  {
    id: "my_trips_easy_to_use",
    text: "The 'My Trips' page is easy to use.",
  },
  {
    id: "my_trips_capabilities_requirements",
    text: "The 'My Trips' page capabilities meet my requirements.",
  },
];

const responseValues = [1, 2, 3, 4, 5];

export const MyTripsCsatSurveyModal = ({
  isOpen,
  onComplete,
  onDismiss,
  onResponse,
}: MyTripsCsatSurveyModalProps) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [selectedValue, setSelectedValue] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRespondedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setStepIndex(0);
      setSelectedValue(null);
      hasRespondedRef.current = false;
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const isThankYouStep = stepIndex >= questions.length;
  const currentQuestion = questions[stepIndex];
  const currentStep = stepIndex + 1;

  const handleDismiss = () => {
    if (hasRespondedRef.current) {
      onComplete();
      return;
    }

    onDismiss(currentStep);
  };

  const handleRatingClick = (responseValue: number) => {
    if (selectedValue != null) {
      return;
    }

    hasRespondedRef.current = true;
    setSelectedValue(responseValue);

    onResponse({
      question: currentQuestion.text,
      questionId: currentQuestion.id,
      responseValue,
      step: currentStep,
    });

    timerRef.current = setTimeout(() => {
      setSelectedValue(null);

      if (stepIndex === questions.length - 1) {
        setStepIndex(questions.length);
        timerRef.current = setTimeout(() => {
          onComplete();
        }, 2500);
        return;
      }

      setStepIndex((previousStepIndex) => previousStepIndex + 1);
    }, 300);
  };

  return (
    <section
      className="my-trips-csat-survey-modal"
      role="dialog"
      aria-labelledby="my-trips-csat-survey-title"
    >
      {!isThankYouStep && (
        <button
          type="button"
          className="my-trips-csat-survey-modal-close"
          aria-label="Dismiss survey"
          onClick={handleDismiss}
        >
          &times;
        </button>
      )}
      {isThankYouStep ? (
        <div className="my-trips-csat-survey-modal-thank-you">
          <img
            src={InnovationBulb}
            alt=""
            className="my-trips-csat-survey-modal-thank-you-icon"
          />
          <h2 id="my-trips-csat-survey-title">Thanks for your feedback!</h2>
        </div>
      ) : (
        <div className="my-trips-csat-survey-modal-content">
          <h2 id="my-trips-csat-survey-title">{currentQuestion.text}</h2>
          <div
            className="my-trips-csat-survey-modal-scale"
            role="radiogroup"
            aria-label={currentQuestion.text}
          >
            {responseValues.map((responseValue) => (
              <button
                key={responseValue}
                type="button"
                className={clsx("my-trips-csat-survey-modal-scale-button", {
                  selected: selectedValue === responseValue,
                })}
                aria-checked={selectedValue === responseValue}
                role="radio"
                onClick={() => handleRatingClick(responseValue)}
              >
                {responseValue}
              </button>
            ))}
          </div>
          <div className="my-trips-csat-survey-modal-labels">
            <span>Strongly disagree</span>
            <span>Strongly agree</span>
          </div>
        </div>
      )}
    </section>
  );
};
